/**
 * The signed-in frame's context, on its own so the root route can ask "am I
 * inside the shell?" without importing the shell itself. That keeps AppShell
 * (and the palette, the kitchen switcher and the rest) out of the entry chunk
 * every visitor to the public site downloads. AppShell provides it and
 * re-exports both hooks.
 */
import { createContext, useContext, useLayoutEffect } from 'react';

export type ShellApi = { setNotFound: (on: boolean) => void };

export const ShellContext = createContext<ShellApi | null>(null);

/** True inside the signed-in frame. The 404 page uses it to render inside
 *  the shell for a signed-in chef without ever nesting a second shell. */
export function useInShell(): boolean {
  return useContext(ShellContext) !== null;
}

/** Called by the 404 page while it shows inside the frame: the tab title
 *  says "Page not found" and no rail link or tab stays lit, even on a path
 *  that looks like a section (a chef on /admin, an admin on a dead
 *  /admin/users/999). A layout effect, so the wrong state never paints. */
export function useShellNotFound(): void {
  const shell = useContext(ShellContext);
  useLayoutEffect(() => {
    if (!shell) return undefined;
    shell.setNotFound(true);
    return () => shell.setNotFound(false);
  }, [shell]);
}
